import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  RadioGroup,
  FormControl,
  FormLabel,
  Radio,
  Box,
  Checkbox,
  CircularProgress,
  InputLabel,
  MenuItem,
  Select,
  ListItemText,
  OutlinedInput,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useOrgan } from '../../../../hooks/useOrgan'
import { organizationPrimaryColors } from '../../../../layouts/PermanentSidebarMUI'
import { useMutation } from '@tanstack/react-query'
import { entities } from '../../../../constants/entities'
import useBaseService from '../../../../hooks/services/useBaseService'
import { useNavigate } from 'react-router-dom'
import useLoader from '../../../../hooks/useLoader'
import { useReport } from '../../../../context/ReportContext'

export const DialogExportTags = ({ data, open, onClose, type = 'equipment' }) => {
  // 🆕 Dimensões padrão diferentes para equipment vs volume
  const [dimensions, setDimensions] = useState(
    type === 'volume' ? '70mmx30mm' : '100mmx150mm'
  )
  const [volumes, setVolumes] = useState([])
  const [selectedVolumeIds, setSelectedVolumeIds] = useState([])
  const [loadingVolumes, setLoadingVolumes] = useState(false)
  const areAllVolumesSelected =
    volumes.length > 0 && selectedVolumeIds.length === volumes.length

  const navigate = useNavigate()
  const { get: baseServiceGet } = useBaseService(navigate)
  const { showLoader, hideLoader } = useLoader()
  const { setExportId, isExporting } = useReport()

  useEffect(() => {
    if (!open || type !== 'volume' || !data?.id) return undefined

    let isActive = true
    setLoadingVolumes(true)

    baseServiceGet(entities.movements.volumes.get(data.id), {
      page: 1,
      perPage: 9999999999,
      sort: { column: 'id', order: 'asc' },
      view: 'lean',
    })
      .then((response) => {
        if (!isActive) return
        const movementVolumes = response?.result?.data ?? []
        setVolumes(movementVolumes)
        setSelectedVolumeIds([])
      })
      .catch((error) => {
        console.error('Erro ao buscar volumes do movimento:', error)
        if (isActive) {
          setVolumes([])
          setSelectedVolumeIds([])
        }
      })
      .finally(() => {
        if (isActive) setLoadingVolumes(false)
      })

    return () => {
      isActive = false
    }
  }, [open, type, data?.id, baseServiceGet])

  const exportTags = useMutation({
    mutationFn: async ({ dimensions, movementId, volumeIds }) => {
      showLoader()
      const params = type === 'volume' ? { volume_ids: volumeIds } : {}
      
      // 🆕 Escolhe o endpoint correto baseado no tipo
      const endpoint = type === 'volume' 
        ? entities.reports.tagVolumes(movementId)
        : entities.reports.tag(movementId)
      
      const response = await baseServiceGet(
        endpoint,
        params,
        { dimensions }
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 202 || response?.status === 200) {
        const generated_id = response.result?.export_id

        if (generated_id) {
          setExportId(generated_id)
          localStorage.setItem('report_exportId', generated_id.toString())
          onClose()
        }
      }
    },
    onError: (error) => {
      console.error('onError ', error)
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
    },
  })

  const handleSubmit = async () => {
    try {
      await exportTags.mutateAsync({
        dimensions,
        movementId: data.id,
        volumeIds: selectedVolumeIds,
      })
    } catch (error) {
      console.error('Erro ao exportar etiquetas:', {
        status: error.status,
        message: error.message,
      })
    }
  }

  const { selectedOrgan } = useOrgan()
  const primaryColor = selectedOrgan?.name
    ? organizationPrimaryColors[selectedOrgan.name.toUpperCase()] ||
      organizationPrimaryColors.ALUCOM
    : organizationPrimaryColors.ALUCOM

  // Opções de dimensão baseadas no tipo
  const dimensionOptions = type === 'volume' 
    ? [
        { label: '(297mm x 210mm)', value: '297mmx210mm' },
        { label: '(210mm x 148mm)', value: '210mmx148mm' },
        { label: '(70mm x 30mm)', value: '70mmx30mm' },
      ]
    : [
        { label: '(100mm x 150mm)', value: '100mmx150mm' },
        { label: '(100mm x 100mm)', value: '100mmx100mm' },
      ]

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      disableEnforceFocus>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: primaryColor,
          color: '#FFFFFF',
        }}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Imprimir Etiquetas {type === 'volume' ? 'de Volumes' : 'de Equipamentos'}
        </DialogTitle>
      </Box>
      <DialogContent>
        <div className="container-fluid p-1">
          {type === 'volume' ? (
            <FormControl fullWidth>
              <InputLabel id="volumes-etiqueta-label" shrink>
                Volumes
              </InputLabel>
              <Select
                labelId="volumes-etiqueta-label"
                id="volumes-etiqueta"
                multiple
                displayEmpty
                value={selectedVolumeIds}
                input={<OutlinedInput label="Volumes" />}
                disabled={loadingVolumes || volumes.length === 0 || isExporting}
                onChange={(event) => {
                  const selectedValue = event.target.value
                  const selected = Array.isArray(selectedValue)
                    ? selectedValue
                    : String(selectedValue).split(',')
                  if (selected.includes('__all_volumes__')) {
                    setSelectedVolumeIds(
                      areAllVolumesSelected
                        ? []
                        : volumes.map((volume) => String(volume.id))
                    )
                    return
                  }
                  setSelectedVolumeIds(selected)
                }}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <em>Selecione um ou mais volumes</em>
                  }
                  if (areAllVolumesSelected) {
                    return `Todos os volumes (${volumes.length})`
                  }
                  return volumes
                    .filter((volume) => selected.includes(String(volume.id)))
                    .map((volume, index) =>
                      volume.name || `Volume ${index + 1}`
                    )
                    .join(', ')
                }}>
                <MenuItem value="__all_volumes__">
                  <Checkbox
                    checked={areAllVolumesSelected}
                    indeterminate={
                      selectedVolumeIds.length > 0 &&
                      selectedVolumeIds.length < volumes.length
                    }
                  />
                  <ListItemText primary="Selecionar todos os volumes" />
                </MenuItem>
                {volumes.map((volume, index) => (
                  <MenuItem key={volume.id} value={String(volume.id)}>
                    <Checkbox
                      checked={selectedVolumeIds.includes(String(volume.id))}
                    />
                    <ListItemText
                      primary={volume.name || `Volume ${index + 1}`}
                      secondary={volume.code ? `Código: ${volume.code}` : undefined}
                    />
                  </MenuItem>
                ))}
              </Select>
              {loadingVolumes && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {!loadingVolumes && volumes.length === 0 && (
                <FormLabel sx={{ mt: 1 }}>
                  Nenhum volume encontrado para este movimento.
                </FormLabel>
              )}
            </FormControl>
          ) : (
            <FormControl>
              <FormLabel id="dimensao-etiqueta" style={{ fontWeight: 'bold' }}>
                Dimensão
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="dimensao-etiqueta"
                name="dimensao-etiqueta"
                value={dimensions}
                onChange={(e) => setDimensions(e.target.value)}>
                {dimensionOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    required
                    control={<Radio />}
                    label={option.label}
                    value={option.value}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="error">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          disabled={
            isExporting ||
            (type === 'volume' &&
              (loadingVolumes || selectedVolumeIds.length === 0))
          }>
          Imprimir
        </Button>
      </DialogActions>
    </Dialog>
  )
}
